import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { capitalize } from '@mui/material'
import { useSecondOrderCallback } from '@chilecompra/react-kit/storages'
import {
  Breadcrumb,
  Breadcrumbs,
  Button,
  Popover,
  PopoverMenuItem,
  Spinner,
  Typography,
  useViewport
} from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onCompareProvidersThunk } from '../Comparator/Comparator.actions'
import { onGetBuyerThunk, onGetBuyerKPIsThunk } from './BuyerSection.actions'
import InfoDataDialog from '../../components/Dialogs/InfoDataDialog'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import {
  Action,
  ActionLink,
  Anchor,
  AnchorIcon,
  AnchorText,
  Card,
  Column,
  Content,
  Datum,
  DownloadIcon,
  ErrorMessage,
  HelpIcon,
  Info,
  Jumbotron,
  Label,
  Line,
  Title,
  Root,
  Row,
  Strong,
  Value
} from './BuyerSection.styles'

/**
 * The BuyerSection's container.
 */
const BuyerSection = props => {
  const { children } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { idEntity } = useParams('idEntity')
  const { biddings, error, loadingBuyer, loadingKPIs, name, orders, sector, totalAmount } = useSelector(
    state => state.buyerSection
  )
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = useMemo(() => generateYearsBetween(endYear, startYear), [])
  const [helpOpen, setHelpOpen] = useState(false)
  const [yearsOpen, setYearsOpen] = useState(false)
  const [yearValue, setYearValue] = useState(endYear)
  const { size } = useViewport()
  const createHandleYearChange = useSecondOrderCallback(
    (_, updatedValue) => {
      setYearValue(updatedValue)
      setYearsOpen(false)
      dispatch(onGetBuyerKPIsThunk({ id: idEntity, year: updatedValue }))
    },
    [idEntity]
  )
  const handleCloseHelp = useCallback(() => setHelpOpen(false), [])
  const handleOpenHelp = useCallback(() => setHelpOpen(true), [])
  const handleCloseYears = useCallback(() => setYearsOpen(false), [])
  const handleOpenYears = useCallback(() => setYearsOpen(true), [])
  const handleRedirectToComparison = useCallback(
    () =>
      dispatch(
        onCompareProvidersThunk({
          callback: () => navigate(routes.comparator),
          firstEntity: name && idEntity ? { name, value: idEntity } : null,
          secondEntity: null
        })
      ),
    [name, idEntity]
  )
  const handleRedirectToDownloads = useCallback(() =>
    navigate(`${routes.downloadOrderAndBiddings}#DescargaReportes`, { state: { idEntity, name } })
  )
  const handleRedirectToHome = useCallback(() => navigate(routes.root, { replace: true }))

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    setYearValue(endYear)
    dispatch(onGetBuyerThunk({ id: idEntity }))
    dispatch(onGetBuyerKPIsThunk({ id: idEntity, year: endYear }))
  }, [endYear, idEntity, initialLoading])

  return (
    <Root height={loadingBuyer || loadingKPIs || !!error ? '100%' : 'auto'}>
      {(initialLoading || loadingBuyer || loadingKPIs) && (
        <Row alignItems="center" height="100%">
          <Column align="center" xs={12}>
            <Spinner />
          </Column>
        </Row>
      )}
      {!!error && !initialLoading && !loadingBuyer && !loadingKPIs && (
        <Row alignItems="center" height="100%">
          <Column align="center" xs={12}>
            <ErrorMessage>
              No existe una ficha de organismo comprador asociada al código de institución “<Strong>{idEntity}</Strong>”
            </ErrorMessage>
            <Button color="primary" onClick={handleRedirectToHome}>
              Volver al Inicio
            </Button>
          </Column>
        </Row>
      )}
      {!error && !initialLoading && !loadingBuyer && !loadingKPIs && (
        <>
          <Row>
            <Column xs={12}>
              <Jumbotron height="288px">
                <Breadcrumbs>
                  <Breadcrumb to={routes.root} variant="link">
                    Datos Abiertos
                  </Breadcrumb>
                  <Breadcrumb to={routes.organizations} variant="link">
                    Organismos Compradores
                  </Breadcrumb>
                  <Breadcrumb to={routes.sector.replace(/:sectorSlug/, sector?.toLowerCase())} variant="link">
                    {capitalize(sector?.toLowerCase() || '')}
                  </Breadcrumb>
                </Breadcrumbs>
                <Title>{name}</Title>
              </Jumbotron>
            </Column>
          </Row>
          <Content>
            <Row margin="0 auto" maxWidth="740px">
              <Column className="textContainer" margin="24px 0 0" md={8} xs={12}>
                <Typography display="inline" fontSize="16px" margin="0 10px 0 0" variant="body2">
                  Cifras acumuladas durante el año
                </Typography>
                <Popover
                  anchor={
                    <Anchor>
                      <AnchorText>{yearValue}</AnchorText>
                      <AnchorIcon open={yearsOpen} />
                    </Anchor>
                  }
                  open={yearsOpen}
                  onClose={handleCloseYears}
                  onOpen={handleOpenYears}
                >
                  {years.map(year => (
                    <PopoverMenuItem enableClose key={year.value} onClick={createHandleYearChange(year.value)}>
                      {year.name}
                    </PopoverMenuItem>
                  ))}
                </Popover>
              </Column>
            </Row>
            <Row margin="0 auto" maxWidth="740px">
              <Column margin="7px 0 0" xs={12}>
                <Card color="blank" elevation="1">
                  <Row spacing={1}>
                    {totalAmount !== undefined ? (
                      <>
                        <Column height="60px" md={5} xs={12}>
                          <Info>
                            <Datum>
                              <Value>${formatAmount(parseInt(totalAmount ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Monto total transado durante el año</Label>
                            </Datum>
                          </Info>
                        </Column>
                        <Column height="60px" md={3} xs={12}>
                          <Info>
                            <Line />
                            <Datum>
                              <Value>{formatAmount(parseInt(orders ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Órdenes de compra</Label>
                            </Datum>
                          </Info>
                        </Column>
                        <Column height="60px" md={4} xs={12}>
                          <Info>
                            <Line />
                            <Datum>
                              <Value>{formatAmount(parseInt(biddings ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Licitaciones adjudicadas</Label>
                            </Datum>
                          </Info>
                        </Column>
                      </>
                    ) : (
                      <>
                        <Column height="30px" md={12} xs={12}>
                          <Typography align="center" color="black2" variant="h4">
                            No existe información para el año seleccionado.
                          </Typography>
                        </Column>
                      </>
                    )}
                  </Row>
                  <Row padding="45px 0 0" spacing={1} alignItems="flex-end">
                    <Column md={9} xs={12}>
                      <Action>
                        <ActionLink onClick={handleOpenHelp}>
                          <HelpIcon>?</HelpIcon>
                          En qué se basa la información de este organismo comprador
                        </ActionLink>
                      </Action>
                      <Action margin="6px 0 0">
                        <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/downloads-page']}>
                          <ActionLink onClick={handleRedirectToDownloads}>
                            <DownloadIcon />
                            Descargas masivas de este organismo
                          </ActionLink>
                        </ProtectByFeatures>
                      </Action>
                    </Column>
                    <Column
                      md={3}
                      textAlign={size.isDownToMedium ? 'center' : 'right'}
                      margin={size.isDownToMedium ? '24px 0 32px' : '0 0 0'}
                      xs={12}
                    >
                      <Button color="primary" variant="outlined" onClick={handleRedirectToComparison}>
                        Comparar
                      </Button>
                    </Column>
                  </Row>
                </Card>
              </Column>
            </Row>
          </Content>
          {children}
          <InfoDataDialog open={helpOpen} onClose={handleCloseHelp} />
        </>
      )}
    </Root>
  )
}

BuyerSection.propTypes = {
  children: PropTypes.node.isRequired
}

export default BuyerSection
